// Detect visitor city once per browser session and cache it.
// Tries (1) explicit override via localStorage, (2) ipapi.co free endpoint, (3) Intl timezone fallback.
// Returns "" when unknown so callers can pass it through to log_ad_event.

import { ETHIOPIAN_CITIES } from '@/lib/ethiopianCities';

const KEY = 'visitor-city-v1';
const TTL_MS = 24 * 60 * 60 * 1000;

type Cached = { city: string; ts: number };

function readCache(): Cached | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const c = JSON.parse(raw) as Cached;
    if (!c || Date.now() - c.ts > TTL_MS) return null;
    return c;
  } catch {
    return null;
  }
}

function writeCache(city: string) {
  try {
    localStorage.setItem(KEY, JSON.stringify({ city, ts: Date.now() }));
  } catch {
    /* ignore */
  }
}

/** Match a free-form city string to one of our supported Ethiopian cities (case-insensitive). */
function normalizeCity(raw: string | null | undefined): string {
  if (!raw) return '';
  const lc = raw.trim().toLowerCase();
  if (!lc) return '';
  const match = ETHIOPIAN_CITIES.find((c) => c.toLowerCase() === lc);
  if (match) return match;
  // Common aliases
  if (lc.includes('addis')) return 'Addis Ababa';
  return '';
}

/** Allow admins/users to override their detected city (used by the preview tool). */
export function setVisitorCityOverride(city: string) {
  writeCache(city || '');
}

export function getCachedVisitorCity(): string {
  return readCache()?.city ?? '';
}

let inflight: Promise<string> | null = null;

export async function detectVisitorCity(): Promise<string> {
  if (typeof window === 'undefined') return '';
  const cached = readCache();
  if (cached) return cached.city;
  if (inflight) return inflight;

  inflight = (async () => {
    let city = '';
    try {
      const r = await fetch('https://ipapi.co/json/', { cache: 'no-store' });
      if (r.ok) {
        const j = await r.json();
        city = normalizeCity(j?.city);
      }
    } catch {
      /* network/blocked → fall through */
    }
    if (!city) {
      try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
        if (tz.toLowerCase().includes('addis')) city = 'Addis Ababa';
      } catch {
        /* ignore */
      }
    }
    writeCache(city);
    inflight = null;
    return city;
  })();

  return inflight;
}
